import { useState, useEffect, useMemo } from "react";
import { getVoiceList, getSampleLanguagePriority } from "../helper/Language_Utils";

// Allowed voices: Chris (male), Roger (male), Laura (female), Jessica (female), Bella (female), River (neutral)
const ALLOWED_VOICE_NAMES = ['Chris', 'Roger', 'Laura', 'Jessica', 'Bella', 'River', 'Sara'];

// Laura's stable ElevenLabs voice_id — IDs are UUIDs that persist even if ElevenLabs
// renames or reorganizes their voice library. Fall back by name, then to first voice.
const DEFAULT_VOICE_ID = 'FGY2WhTYpPnrIDTdsKH5'; // Laura
const DEFAULT_VOICE_NAME = 'Laura';
const LANGUAGE_DEFAULT_VOICE_MAP = {
    'ar': 'gMB389pj77Qe5nErWNjd', //Sara
    'apc': 'gMB389pj77Qe5nErWNjd', //Sara
}

const VoiceSelector = ({ voice, onVoiceChange, disabled, playbackRate, language, groupClassName,
    labelClassName, selectClassName, sampleClassName, sampleAudioClassName }) => {
    const [availableVoices, setAvailableVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState(voice ?? "");
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (availableVoices.length > 0) {
            return;
        }
        setIsLoading(true);
        getVoiceList().then((voices) => {
            // Filter to only allowed voices (case-insensitive)
            const filteredVoices = voices.filter(v =>
                ALLOWED_VOICE_NAMES.some(allowedName =>
                    v.name.toLowerCase() === allowedName.toLowerCase()
                )
            );

            // If filtering results in no voices, use all voices as fallback
            const voicesToUse = filteredVoices.length > 0 ? filteredVoices : voices;

            console.log('Available voices:', voicesToUse.map(v => v.name));

            setAvailableVoices(voicesToUse);
            if (voicesToUse.length > 0 && (!selectedVoice || !voicesToUse.find(v => v.voice_id === selectedVoice))) {
                const byId = voicesToUse.find(v => v.voice_id === DEFAULT_VOICE_ID);
                const byName = voicesToUse.find(v => v.name.toLowerCase() === DEFAULT_VOICE_NAME.toLowerCase());
                const defaultVoice = (byId || byName || voicesToUse[0]).voice_id;
                setSelectedVoice(defaultVoice);
                if (onVoiceChange) {
                    onVoiceChange(defaultVoice);
                }
            }
            setIsLoading(false);
        }).catch((error) => {
            console.error("Error loading voices:", error);
            setIsLoading(false);
        });
    }, [availableVoices.length, selectedVoice, onVoiceChange]);
    const getFilteredVoices = () => {
        return availableVoices.filter(v => v.allowedLanguages?.includes(language) || !v.allowedLanguages);
    };
    useEffect(() => {
        if (LANGUAGE_DEFAULT_VOICE_MAP[language]) {
            setSelectedVoice(LANGUAGE_DEFAULT_VOICE_MAP[language]);
        }
        else if (!getFilteredVoices().find(v => v.voice_id === LANGUAGE_DEFAULT_VOICE_MAP[language])) {
            setSelectedVoice(DEFAULT_VOICE_ID);
        }
    }, [language, availableVoices])

    const getVoiceSample = (voice, lang) => {
        lang = lang || "en";
        const clip_langs = getSampleLanguagePriority(lang);
        for (const l of clip_langs) {
            if (voice?.language?.[l]?.sample) {
                return voice?.language?.[l]?.sample;
            }
        }
        for (const l of Object.keys(voice?.language ?? {})) { // fallback to first available sample
            if (voice?.language?.[l]?.sample) {
                return voice?.language?.[l]?.sample;
            }
        }
        return null;
    };
    const sampleAudio = getVoiceSample(availableVoices.find(v => v.voice_id === selectedVoice), language);

    if (isLoading) {
        return <div className={groupClassName}>Loading voices...</div>;
    }

    if (availableVoices.length === 0) {
        return <div className={groupClassName}>No voices available</div>;
    }

    return (
        <>
            <div className={groupClassName}>
                <label htmlFor="voice-select" className={labelClassName}>
                    Voice ({getFilteredVoices().length} available)
                </label>
                <select
                    id="voice-select"
                    className={selectClassName}
                    value={selectedVoice}
                    onChange={(e) => { setSelectedVoice(e.target.value); onVoiceChange?.(e.target.value); }}
                    disabled={disabled}
                >
                    {selectedVoice && availableVoices.find(v => v.voice_id === selectedVoice) && !getFilteredVoices().find(v => v.voice_id === selectedVoice) && (
                        <option value={selectedVoice} disabled>{availableVoices.find(v => v.voice_id === selectedVoice).name}</option>
                    )}
                    {getFilteredVoices().length > 0 ? (
                        getFilteredVoices().map((voice) => (
                            <option key={voice.voice_id} value={voice.voice_id}>
                                {voice.name} ({voice.gender})
                            </option>
                        ))
                    ) : (
                        <option value="">Default</option>
                    )}
                </select>
                {sampleAudio ? (
                    <div className={sampleClassName}>
                        <audio src={sampleAudio} preload="none" controlsList="nodownload nofullscreen noplaybackrate"
                            controls className={sampleAudioClassName} onPlay={(e) => e.target.playbackRate = playbackRate} />
                    </div>
                ) : (
                    <div className={sampleClassName}>
                        No sample available
                    </div>
                )}
            </div>
        </>
    )
};

export default VoiceSelector;